import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import useScrollAnimation from '../hooks/useScrollAnimation';

const recruitDirections = [
  {
    title: "Web",
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10"/>
        <path d="M2 12h20"/>
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
      </svg>
    ),
    color: "#8B4513",
    description: "Web安全是网络安全的重要入口。学习前端技术、后端漏洞挖掘、渗透测试，掌握SQL注入、XSS、CSRF等经典漏洞原理与利用。",
    skills: ["前端开发", "漏洞挖掘", "渗透测试", "代码审计"],
    difficulty: "入门友好",
    badge: "热门"
  },
  {
    title: "Pwn",
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
      </svg>
    ),
    color: "#CD5C5C",
    description: "二进制漏洞利用的精髓。学习C/C++底层原理、内存管理、缓冲区溢出、ROP链构造，挑战CTF竞赛中的Pwn题目。",
    skills: ["逆向工程", "漏洞利用", "汇编语言", "动态调试"],
    difficulty: "需要基础",
    badge: "核心"
  },
  {
    title: "Reverse",
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="11" cy="11" r="8"/>
        <path d="m21 21-4.3-4.3"/>
        <path d="M11 8v6"/>
        <path d="M8 11h6"/>
      </svg>
    ),
    color: "#DAA520",
    description: "逆向分析是安全研究的基石。学习IDA Pro、Ghidra等工具使用，分析恶意软件、破解程序逻辑、还原算法原理。",
    skills: ["静态分析", "动态调试", "脱壳技术", "算法还原"],
    difficulty: "需要耐心",
    badge: "核心"
  },
  {
    title: "Crypto",
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
      </svg>
    ),
    color: "#8B4513",
    description: "密码学是信息安全的数学基础。学习古典密码、现代加密算法、数论基础，参与密码分析与挑战。",
    skills: ["数论基础", "加密算法", "密码分析", "协议安全"],
    difficulty: "数学友好",
    badge: "研究"
  },
  {
    title: "DevOps",
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 2v20M2 12h20M4.93 4.93l14.14 14.14M19.07 4.93L4.93 19.07"/>
      </svg>
    ),
    color: "#CD5C5C",
    description: "安全运维与自动化。学习Docker、K8s容器安全、CI/CD流程、自动化脚本开发，构建安全的基础设施。",
    skills: ["容器安全", "自动化运维", "脚本开发", "云平台"],
    difficulty: "入门友好",
    badge: "新兴"
  },
  {
    title: "Design",
    icon: (
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 19l7-7 3 3-7 7-3-3z"/>
        <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z"/>
        <path d="M2 2l7.586 7.586"/>
        <circle cx="11" cy="11" r="2"/>
      </svg>
    ),
    color: "#DAA520",
    description: "安全与设计的跨界融合。学习UI/UX设计、安全产品界面设计、数据可视化、安全宣传物料制作。",
    skills: ["UI设计", "数据可视化", "品牌设计", "前端开发"],
    difficulty: "创意导向",
    badge: "特色"
  }
];

const getDifficultyStyle = (difficulty) => {
  switch(difficulty) {
    case '入门友好':
      return { bg: '#8B4513', color: '#8B4513' };
    case '需要基础':
      return { bg: '#CD5C5C', color: '#CD5C5C' };
    case '需要耐心':
      return { bg: '#DAA520', color: '#DAA520' };
    case '数学友好':
      return { bg: '#8B4513', color: '#8B4513' };
    case '创意导向':
      return { bg: '#DAA520', color: '#DAA520' };
    default:
      return { bg: '#6B6B6B', color: '#6B6B6B' };
  }
};

const getBadgeStyle = (badge) => {
  switch(badge) {
    case '热门':
      return { bg: '#CD5C5C', shadow: 'rgba(205, 92, 92, 0.3)' };
    case '核心':
      return { bg: '#DAA520', shadow: 'rgba(218, 165, 32, 0.3)' };
    case '研究':
      return { bg: '#DAA520', shadow: 'rgba(218, 165, 32, 0.3)' };
    case '新兴':
      return { bg: '#8B4513', shadow: 'rgba(139, 69, 19, 0.3)' };
    case '特色':
      return { bg: '#DAA520', shadow: 'rgba(218, 165, 32, 0.3)' };
    default:
      return { bg: '#6B6B6B', shadow: 'rgba(107, 107, 107, 0.3)' };
  }
};

function DirectionsSection() {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [selectedDirection, setSelectedDirection] = useState(null);
  const [sectionRef, isSectionVisible] = useScrollAnimation(0.1);
  const [revealedDirections, setRevealedDirections] = useState([]);
  const [isRevealing, setIsRevealing] = useState(false);

  useEffect(() => {
    if (isSectionVisible && revealedDirections.length === 0 && !isRevealing) {
      setIsRevealing(true);
      
      const revealNext = () => {
        setRevealedDirections(prev => {
          if (prev.length >= recruitDirections.length) {
            setIsRevealing(false);
            return prev;
          }
          const newIndex = prev.length;
          return [...prev, newIndex];
        });
        
        setTimeout(() => {
          setRevealedDirections(prev => {
            if (prev.length < recruitDirections.length) {
              revealNext();
            } else {
              setIsRevealing(false);
            }
            return prev;
          });
        }, 300);
      };
      
      revealNext();
    }
  }, [isSectionVisible, revealedDirections.length, isRevealing]);

  return (
    <section ref={sectionRef} id="招新方向" className="section directions-section" aria-labelledby="directions-title" style={{
      background: '#FFFFFF',
      position: 'relative',
      overflow: 'hidden',
      opacity: isSectionVisible ? 1 : 0,
      transform: isSectionVisible ? 'translateY(0)' : 'translateY(50px)',
      transition: 'all 1s cubic-bezier(0.23, 1, 0.32, 1)',
      padding: 'var(--space-16) var(--space-8)'
    }}>
      <div className="container" style={{ position: 'relative', zIndex: 1, maxWidth: '1200px', margin: '0 auto' }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 
            id="directions-title" 
            style={{
              fontSize: 'clamp(2rem, 5vw, 3rem)',
              fontWeight: '700',
              color: '#2C2C2C',
              fontFamily: 'serif',
              marginBottom: 'var(--space-4)',
              textAlign: 'center'
            }}
          >
            招新方向
          </h2>
          <p style={{
            textAlign: 'center',
            color: '#6B6B6B',
            fontSize: '1.1rem',
            marginTop: 'var(--space-4)',
            marginBottom: 'var(--space-12)'
          }}>
            六大技术方向，总有一个适合你
          </p>
        </motion.div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
          gap: 'var(--space-6)',
          marginTop: 'var(--space-8)'
        }}>
          {recruitDirections.map((direction, index) => {
            const difficultyStyle = getDifficultyStyle(direction.difficulty);
            const badgeStyle = getBadgeStyle(direction.badge);
            const isHovered = hoveredCard === index;
            const isSelected = selectedDirection === index;
            const isRevealed = revealedDirections.includes(index);

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                onHoverStart={() => setHoveredCard(index)}
                onHoverEnd={() => setHoveredCard(null)}
                onClick={() => setSelectedDirection(selectedDirection === index ? null : index)}
                style={{
                  position: 'relative',
                  padding: 'var(--space-6)',
                  cursor: 'pointer',
                  transform: isHovered ? 'translateY(-4px)' : 'translateY(0)',
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  overflow: 'hidden'
                }}
              >
                <AnimatePresence>
                  {!isRevealed && (
                    <motion.div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        background: '#FFFFFF',
                        zIndex: 3
                      }}
                      exit={{ opacity: 0, y: -50 }}
                      transition={{ duration: 0.5 }}
                    />
                  )}
                </AnimatePresence>

                <div style={{
                  position: 'absolute',
                  top: 'var(--space-4)',
                  right: 'var(--space-4)',
                  color: badgeStyle.bg,
                  padding: 'var(--space-1) var(--space-3)',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  zIndex: 2
                }}>
                  {direction.badge}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
                  <motion.div
                    style={{
                      width: '60px',
                      height: '60px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: direction.color
                    }}
                    animate={{ 
                      scale: isHovered ? 1.1 : 1
                    }}
                    transition={{ duration: 0.3 }}
                  >
                    {direction.icon}
                  </motion.div>
                  <div>
                    <h3 style={{
                      fontSize: '1.6rem',
                      fontWeight: '700',
                      color: '#2C2C2C',
                      margin: 0,
                      fontFamily: 'serif'
                    }}>
                      {direction.title}
                    </h3>
                    <div style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 'var(--space-1)',
                      marginTop: 'var(--space-1)',
                      padding: 'var(--space-1) var(--space-2)',
                      fontSize: '0.75rem',
                      color: difficultyStyle.color,
                      fontWeight: '500'
                    }}>
                      <span style={{ 
                        width: '6px', 
                        height: '6px', 
                        background: difficultyStyle.color 
                      }} />
                      {direction.difficulty}
                    </div>
                  </div>
                </div>

                <p style={{
                  fontSize: '0.95rem',
                  lineHeight: '1.6',
                  color: '#4A4A4A',
                  marginBottom: 'var(--space-4)'
                }}>
                  {direction.description}
                </p>

                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 'var(--space-2)'
                }}>
                  {direction.skills.map((skill, skillIndex) => (
                    <motion.span
                      key={skillIndex}
                      style={{
                        padding: 'var(--space-1) var(--space-3)',
                        color: direction.color,
                        fontSize: '0.8rem',
                        fontWeight: '500'
                      }}
                      whileHover={{ 
                        scale: 1.05
                      }}
                    >
                      {skill}
                    </motion.span>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export default DirectionsSection;